import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PcRecommendedCallout } from '@/components/PcRecommendedCallout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { PageSetupProgressCard } from '@/components/PageSetupProgressCard';
import { useSession } from '@/hooks/useSession';
import {
  inputClass,
  settingsPanelClass,
  inlineSuccessClass,
  inlineErrorClass,
  toastBarClass,
} from '@/components/ui/inputStyles';

type WechatAuthMode = 'component' | 'mp_secret';

type LinkedSiteBrief = { id: number; site_name: string };

type WechatAccountRow = {
  id: number;
  authorizer_appid: string;
  /** 扫码授权后由开放平台接口写入；AppSecret 方式保存后由公众平台接口写入 */
  authorizer_nick_name?: string;
  auth_mode: WechatAuthMode;
  /** 当前工作站点是否开启向该号推草稿 */
  push_enabled: boolean;
  site_linked?: boolean;
  authorizer_expires_at: string;
  linked_sites?: LinkedSiteBrief[];
};

type Status = {
  ok: boolean;
  site_id: number;
  wechat_auth_mode: WechatAuthMode;
  mp_secret_configured: boolean;
  component_configured: boolean;
  component_appsecret_configured?: boolean;
  has_component_verify_ticket: boolean;
  authorizer_appid: string;
  authorizer_expires_at: string;
  wechat_accounts?: WechatAccountRow[];
  /** 当前用户已授权公众号数量（去重后） */
  wechat_account_count?: number;
  /** 当前站点已开启推送的公众号数 */
  site_push_enabled_count?: number;
  /** 每用户授权上限 */
  wechat_account_max?: number;
  pre_auth_url: string;
  pre_auth_error?: string;
  redirect_uri_hint: string;
  redirect_uri_host?: string;
  site_url?: string;
  state_for_this_session: string;
};

function formatExpiresAt(raw: string): string {
  const s = (raw || '').trim();
  if (!s) return '—';
  return s.length > 16 ? s.slice(0, 16) : s;
}

/**
 * 与 `api/lib/wechat_open.php` 中 `wes_site_wechat_account_max()` 的返回值兜底一致；
 * 首屏或请求失败时使用；正常运行时以接口字段 `wechat_account_max` 为准。
 */
const WECHAT_ACCOUNT_MAX_FALLBACK = 200;

export function WechatPage() {
  const { currentSiteId, loading: sessionLoading } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<Status | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [mode, setMode] = useState<WechatAuthMode>('component');
  const [mpAppid, setMpAppid] = useState('');
  const [mpAppsecret, setMpAppsecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [oauthOkBanner, setOauthOkBanner] = useState<string | null>(null);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);
  const [busyAccountId, setBusyAccountId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WechatAccountRow | null>(null);
  const [deletingAccountId, setDeletingAccountId] = useState<number | null>(null);
  /** 最近一次 `get_wechat_bind_status` 返回的 `wechat_account_max`，供异步回调与首屏兜底文案使用 */
  const wechatAccountMaxRef = useRef(WECHAT_ACCOUNT_MAX_FALLBACK);

  const load = useCallback(
    async (opts?: { quiet?: boolean }): Promise<Status | null> => {
      if (currentSiteId < 1) return null;
      if (!opts?.quiet) {
        setApiLoading(true);
        setSaveMsg(null);
      }
      try {
        const r = await apiFetch<Status>('get_wechat_bind_status', {
          method: 'GET',
          query: { site_id: currentSiteId },
        });
        if (r.ok) {
          const mx =
            typeof r.wechat_account_max === 'number' && r.wechat_account_max > 0
              ? r.wechat_account_max
              : WECHAT_ACCOUNT_MAX_FALLBACK;
          wechatAccountMaxRef.current = mx;
          setData(r);
          setMode(r.wechat_auth_mode === 'mp_secret' ? 'mp_secret' : 'component');
          setMpAppid(r.authorizer_appid || '');
          setMpAppsecret('');
          return r;
        }
      } finally {
        if (!opts?.quiet) setApiLoading(false);
      }
      return null;
    },
    [currentSiteId],
  );

  useEffect(() => {
    if (sessionLoading) return;
    void load();
  }, [sessionLoading, load]);

  useEffect(() => {
    if (searchParams.get('wechat_auth_ok') !== '1') return;
    navigate('/wechat', { replace: true });
    setOauthOkBanner('已更新公众号授权。同一 AppID 在您名下仅保留最新一条；请在下方为当前站点勾选需要接收草稿的公众号。');
    void load();
  }, [searchParams, navigate, load]);

  useEffect(() => {
    if (searchParams.get('wechat_auth_err') !== 'max_accounts') return;
    navigate('/wechat', { replace: true });
    setOauthOkBanner(null);
    void load({ quiet: true }).then(() => {
      const m = wechatAccountMaxRef.current;
      setSaveMsg(`您的公众号授权已达上限（每个用户最多 ${m} 个），请先移除不需要的公众号后再扫码添加。`);
    });
  }, [searchParams, navigate, load]);

  const saveMode = async () => {
    if (currentSiteId < 1) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      if (mode === 'mp_secret') {
        const r = await apiFetch<{ ok: boolean; err?: string; token_ok?: boolean; detail?: string }>(
          'save_wechat_auth_settings',
          {
            method: 'POST',
            body: JSON.stringify({
              site_id: currentSiteId,
              wechat_auth_mode: 'mp_secret',
              mp_appid: mpAppid.trim(),
              mp_appsecret: mpAppsecret.trim(),
            }),
          },
        );
        if (r.ok) {
          setSaveMsg(
            r.token_ok === false ? '已保存，但暂时连不上微信，请核对 AppID 与 AppSecret。' : '已保存，公众号已接通。',
          );
          setMpAppsecret('');
          await load({ quiet: true });
        } else if (r.err === 'wechat_accounts_limit') {
          setSaveMsg(typeof r.detail === 'string' && r.detail !== '' ? r.detail : '您的公众号授权已达上限。');
        } else {
          setSaveMsg('保存失败，请稍后重试。');
        }
      } else {
        const r = await apiFetch<{ ok: boolean; err?: string }>('save_wechat_auth_settings', {
          method: 'POST',
          body: JSON.stringify({
            site_id: currentSiteId,
            wechat_auth_mode: 'component',
          }),
        });
        if (r.ok) {
          const cap = data?.wechat_account_max ?? wechatAccountMaxRef.current;
          setSaveMsg(
            `已保存为扫码授权方式。公众号授权与站点相互独立：扫码写入您的授权列表，各站点自行勾选要推送到哪些公众号草稿箱。`,
          );
          await load({ quiet: true });
        }
      }
    } catch (e) {
      setSaveMsg(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const setAccountPush = async (accountId: number, pushEnabled: boolean) => {
    if (currentSiteId < 1) return;
    setBusyAccountId(accountId);
    setSaveMsg(null);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_wechat_account_push_enabled', {
        method: 'POST',
        body: JSON.stringify({
          site_id: currentSiteId,
          account_id: accountId,
          push_enabled: pushEnabled,
        }),
      });
      if (r.ok) {
        await load({ quiet: true });
      }
    } catch (e) {
      setSaveMsg(e instanceof Error ? e.message : '更新失败');
    } finally {
      setBusyAccountId(null);
    }
  };

  const accountDisplayName = (a: WechatAccountRow) =>
    (a.authorizer_nick_name || '').trim() || a.authorizer_appid || `#${a.id}`;

  const confirmDeleteAccount = async () => {
    if (!deleteTarget || currentSiteId < 1) return;
    const accountId = deleteTarget.id;
    const label = accountDisplayName(deleteTarget);
    setDeletingAccountId(accountId);
    setSaveMsg(null);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('delete_wechat_account', {
        method: 'POST',
        body: JSON.stringify({ site_id: currentSiteId, account_id: accountId }),
      });
      if (r.ok) {
        setDeleteTarget(null);
        setData((prev) =>
          prev
            ? {
                ...prev,
                wechat_accounts: (prev.wechat_accounts ?? []).filter((a) => a.id !== accountId),
                wechat_account_count: Math.max(
                  0,
                  (typeof prev.wechat_account_count === 'number' ? prev.wechat_account_count : (prev.wechat_accounts ?? []).length) - 1,
                ),
              }
            : prev,
        );
        setSaveMsg(`已删除公众号「${label}」`);
        await load({ quiet: true });
      } else {
        setSaveMsg(r.err === 'not_found' ? '公众号不存在或无权操作。' : '删除失败，请稍后重试。');
      }
    } catch (e) {
      setSaveMsg(e instanceof Error ? e.message : '删除失败');
    } finally {
      setDeletingAccountId(null);
    }
  };

  async function copyPreAuthUrl(url: string) {
    setCopyMsg(null);
    try {
      await navigator.clipboard.writeText(url);
      setCopyMsg('已复制到剪贴板');
      window.setTimeout(() => setCopyMsg(null), 2000);
    } catch {
      setCopyMsg('复制失败，请长按下方按钮打开的页面地址栏复制');
      window.setTimeout(() => setCopyMsg(null), 4000);
    }
  }

  const showMain = !sessionLoading && currentSiteId >= 1 && !apiLoading;

  const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const cfgHost = (data?.redirect_uri_host || '').trim();
  const hostMismatch =
    Boolean(data) &&
    cfgHost !== '' &&
    browserHost !== '' &&
    browserHost.toLowerCase() !== cfgHost.toLowerCase();

  const accounts = (data?.wechat_accounts ?? []).filter((a) => a && a.id > 0);
  const accMax = data?.wechat_account_max ?? wechatAccountMaxRef.current;
  const accCount =
    typeof data?.wechat_account_count === 'number' ? data.wechat_account_count : accounts.length;
  const atAccountLimit = Boolean(data && data.wechat_auth_mode === 'component' && accCount >= accMax);
  const preAuthUsable = Boolean(data?.pre_auth_url) && !atAccountLimit;
  const sitePushCount =
    typeof data?.site_push_enabled_count === 'number' ? data.site_push_enabled_count : accounts.filter((a) => a.push_enabled).length;
  const saveMsgLooksError = Boolean(saveMsg && /上限|失败|无权/.test(saveMsg));
  const deleteTargetLabel = deleteTarget ? accountDisplayName(deleteTarget) : '';

  return (
    <div className="space-y-8 pb-10">
      <PageHeader
        title="微信公众号"
        description={`公众号授权与站点相互独立：您名下的公众号可被多个站点勾选用于推送草稿。当前站点仅向已开启「本站点推送」的账号写入草稿；请在公众平台选稿发布。每个用户最多授权 ${accMax} 个公众号（按 AppID 去重）。`}
      />

      <PcRecommendedCallout>
        授权链接、回调域名与密钥核对步骤较多，建议在电脑浏览器中完成绑定与排查。
      </PcRecommendedCallout>

      <PageSetupProgressCard
        sessionLoading={sessionLoading}
        currentSiteId={currentSiteId}
        apiLoading={apiLoading}
        slowRequestHint="若使用「扫码授权」，服务器需要与微信平台通信并生成入口，可能需要数秒到十余秒，请稍候。长时间无响应时再刷新页面或联系管理员。"
      />

      {showMain && oauthOkBanner ? (
        <div className={inlineSuccessClass}>
          <span className="font-medium">
            {data && data.wechat_auth_mode === 'component' && (data.wechat_account_count ?? 0) >= accMax
              ? `授权成功。您的公众号授权已达上限（每个用户最多 ${accMax} 个），请先移除旧号后再添加。`
              : oauthOkBanner}
          </span>
          <button
            type="button"
            className="ml-3 text-emerald-800/80 underline decoration-emerald-600/30 underline-offset-2 hover:text-emerald-900"
            onClick={() => setOauthOkBanner(null)}
          >
            关闭
          </button>
        </div>
      ) : null}

      {showMain && data ? (
        <>
          {/* 概览条 */}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white to-slate-50/80 px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">接入方式</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {data.wechat_auth_mode === 'mp_secret' ? 'AppID + Secret' : '扫码授权'}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white to-teal-50/40 px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">我的公众号（去重）</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {accCount} / {accMax}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">仅显示当前用户授权，同一 AppID 仅保留最新一条</p>
            </div>
            <div className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white to-emerald-50/50 px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">本站点推送</p>
              <p className="mt-1 text-lg font-semibold text-emerald-900">{sitePushCount} 个</p>
              <p className="mt-0.5 text-xs text-slate-500">当前站点写入草稿的目标号</p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-5">
            <div className="space-y-6 lg:col-span-3">
              <Card title="接入方式">
                <div className="space-y-5">
                  <p className="text-sm leading-relaxed text-slate-600">
                    选择本站点偏好的接入方式（仅影响如何新增授权）。扫码写入您的公众号列表；Secret 适合单号且自行保管密钥。各站点可独立勾选要推送的公众号。
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    <label
                      className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 shadow-sm transition sm:min-w-[220px] ${
                        mode === 'component'
                          ? 'border-teal-400/70 bg-teal-50/50 ring-1 ring-teal-500/20'
                          : 'border-slate-200/85 bg-white/95 hover:border-teal-200/80 hover:shadow-md'
                      }`}
                    >
                      <input
                        type="radio"
                        name="wechat_auth_mode"
                        className="mt-1"
                        checked={mode === 'component'}
                        onChange={() => setMode('component')}
                      />
                      <span>
                        <span className="font-medium text-slate-900">扫码授权（推荐）</span>
                        <span className="mt-1 block text-sm text-slate-600">
                          管理员微信扫码写入您的授权列表；同一 AppID 重复扫码会刷新令牌并覆盖旧记录。
                        </span>
                      </span>
                    </label>
                    <label
                      className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 shadow-sm transition sm:min-w-[220px] ${
                        mode === 'mp_secret'
                          ? 'border-teal-400/70 bg-teal-50/50 ring-1 ring-teal-500/20'
                          : 'border-slate-200/85 bg-white/95 hover:border-teal-200/80 hover:shadow-md'
                      }`}
                    >
                      <input
                        type="radio"
                        name="wechat_auth_mode"
                        className="mt-1"
                        checked={mode === 'mp_secret'}
                        onChange={() => setMode('mp_secret')}
                      />
                      <span>
                        <span className="font-medium text-slate-900">AppID + AppSecret</span>
                        <span className="mt-1 block text-sm text-slate-600">
                          公众平台「设置与开发 → 基本配置」；切换到此方式将移除已保存的扫码授权记录。
                        </span>
                      </span>
                    </label>
                  </div>

                  {mode === 'mp_secret' ? (
                    <div className={`space-y-4 ${settingsPanelClass}`}>
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-slate-600">AppID</label>
                        <input
                          type="text"
                          value={mpAppid}
                          onChange={(e) => setMpAppid(e.target.value)}
                          className={inputClass}
                          placeholder="wx……"
                          autoComplete="off"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-slate-600">AppSecret</label>
                        <input
                          type="password"
                          value={mpAppsecret}
                          onChange={(e) => setMpAppsecret(e.target.value)}
                          className={inputClass}
                          placeholder={data?.mp_secret_configured ? '留空表示不修改' : '必填'}
                          autoComplete="new-password"
                        />
                      </div>
                      <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMode()}>
                        {saving ? '保存中…' : '保存'}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <Button type="button" variant="secondary" disabled={saving} onClick={() => void saveMode()}>
                        {saving ? '保存中…' : '保存当前选择'}
                      </Button>
                    </div>
                  )}

                  {saveMsg ? (
                    <p className={saveMsgLooksError ? `text-sm ${inlineErrorClass}` : 'text-sm text-slate-700'}>{saveMsg}</p>
                  ) : null}
                </div>
              </Card>

              <Card title="我的公众号与站点推送">
                {accounts.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-8 text-center">
                    <p className="text-sm text-slate-600">您尚未授权任何公众号。</p>
                    {data.wechat_auth_mode === 'component' && data.pre_auth_url ? (
                      <p className="mt-2 text-xs text-slate-500">请使用下方「微信扫码绑定」完成首个公众号授权。</p>
                    ) : null}
                    {data.wechat_auth_mode === 'mp_secret' ? (
                      <p className="mt-2 text-xs text-slate-500">请在上方填写 AppID 与 AppSecret 并保存。</p>
                    ) : null}
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200/90 bg-white">
                    {accounts.map((a) => (
                      <li
                        key={a.id}
                        className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-base font-semibold tracking-tight text-slate-900">
                            {(a.authorizer_nick_name || '').trim() || '（未获取名称，可重新保存或重新扫码）'}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <code className="rounded-lg bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700 sm:text-sm">
                              AppID：{a.authorizer_appid || '—'}
                            </code>
                            <span
                              className={
                                a.auth_mode === 'mp_secret'
                                  ? 'rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800'
                                  : 'rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800'
                              }
                            >
                              {a.auth_mode === 'mp_secret' ? 'Secret' : '扫码'}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-slate-500">
                            令牌参考过期：{formatExpiresAt(a.authorizer_expires_at)}
                          </p>
                          {(a.linked_sites?.length ?? 0) > 0 ? (
                            <p className="mt-1.5 text-[11px] text-slate-600">
                              已开启推送的站点：{a.linked_sites!.map((s) => s.site_name).join('、')}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-shrink-0 flex-wrap items-center gap-3 sm:justify-end">
                          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                            <span className="select-none">本站点推送</span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={a.push_enabled}
                              disabled={busyAccountId === a.id}
                              onClick={() => void setAccountPush(a.id, !a.push_enabled)}
                              className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${
                                a.push_enabled ? 'bg-teal-500' : 'bg-slate-300'
                              } ${busyAccountId === a.id ? 'opacity-60' : ''}`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-6 w-6 translate-y-0.5 rounded-full bg-white shadow transition ${
                                  a.push_enabled ? 'translate-x-5' : 'translate-x-0.5'
                                }`}
                              />
                            </button>
                          </label>
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            disabled={busyAccountId === a.id || deletingAccountId === a.id}
                            onClick={() => setDeleteTarget(a)}
                            aria-label={`删除公众号 ${accountDisplayName(a)}`}
                          >
                            <i className="fa-regular fa-trash-can" aria-hidden />
                            {deletingAccountId === a.id ? '删除中…' : '删除'}
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-3 text-xs leading-relaxed text-slate-500">
                  「本站点推送」仅控制当前站点是否向该号写草稿；您名下的其它站点可独立勾选同一公众号。「删除」将从您的账号下移除该公众号授权（所有站点均无法再推送，需重新扫码或填写 Secret 才能恢复）。金数据入库与「管理登记」手动推稿，仅对<strong className="text-slate-700">本站点已开启</strong>的公众号生效。
                </p>
              </Card>
            </div>

            <div className="space-y-6 lg:col-span-2">
              <Card title="微信扫码绑定">
                {data.wechat_auth_mode === 'component' ? (
                  preAuthUsable ? (
                    <div className="space-y-4">
                      {hostMismatch ? (
                        <div className={toastBarClass}>
                          请使用与后台一致的网址访问本页后再扫码；若您从其它地址打开，可能无法完成授权。
                        </div>
                      ) : null}
                      <p className="text-sm text-slate-600">
                        用<strong className="text-slate-800">公众号管理员</strong>微信扫码。写入您的授权列表；同一 AppID 重复扫码会保留最新授权并清理旧记录。
                      </p>
                      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                        <a
                          href={data.pre_auth_url}
                          target="_blank"
                          rel="noopener"
                          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-teal-500 to-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-teal-900/20 ring-1 ring-teal-500/25 transition hover:from-teal-400 hover:to-teal-600"
                        >
                          打开微信授权页
                        </a>
                        <Button type="button" variant="secondary" size="sm" onClick={() => void copyPreAuthUrl(data.pre_auth_url)}>
                          复制授权链接
                        </Button>
                      </div>
                      {copyMsg ? <p className="text-xs text-slate-500">{copyMsg}</p> : null}
                      <p className="text-xs text-slate-500">请从本页点击打开，勿将链接粘贴到浏览器地址栏直接访问。</p>
                    </div>
                  ) : data.pre_auth_url && atAccountLimit ? (
                    <div className="space-y-3">
                      <div className={inlineErrorClass}>
                        您的公众号授权已达上限（每个用户最多 {accMax} 个），无法生成新的授权入口。请先移除列表中不需要的公众号后再试。
                      </div>
                      <p className="text-xs text-slate-500">若仍显示授权按钮，请刷新页面以同步服务器状态。</p>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm text-amber-900">
                      <p>暂时无法生成授权入口，请联系管理员检查配置。</p>
                      {data.pre_auth_error ? (
                        <p className={toastBarClass + ' text-xs leading-relaxed'}>{data.pre_auth_error}</p>
                      ) : null}
                    </div>
                  )
                ) : (
                  <p className="text-sm text-slate-600">
                    当前为 AppID + AppSecret 方式，无需扫码。若改回扫码授权，请在左侧选择「扫码授权」并保存（将清除 Secret 方式在本站保存的绑定）。
                  </p>
                )}
              </Card>

              <div className="rounded-2xl border border-slate-200/80 bg-slate-50/80 p-4 text-xs leading-relaxed text-slate-600">
                <p className="font-medium text-slate-800">提示</p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  <li>回调地址：<code className="rounded bg-white px-1">{data.redirect_uri_hint || '—'}</code></li>
                  <li>与浏览器地址栏域名不一致时，扫码可能失败。</li>
                  <li>多个站点可勾选同一公众号；建议在名称或用途上区分草稿来源。</li>
                </ul>
              </div>
            </div>
          </div>
          <Modal
            open={deleteTarget !== null}
            title="删除公众号"
            onClose={() => {
              if (deletingAccountId !== null) return;
              setDeleteTarget(null);
            }}
            wide
            footer={
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={deletingAccountId !== null}
                  onClick={() => setDeleteTarget(null)}
                >
                  取消
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  disabled={deletingAccountId !== null}
                  onClick={() => void confirmDeleteAccount()}
                >
                  {deletingAccountId !== null ? '删除中…' : '确认删除'}
                </Button>
              </div>
            }
          >
            {deleteTarget ? (
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>
                  确定删除公众号 <strong className="text-slate-900">{deleteTargetLabel}</strong>？
                </p>
                {deleteTarget.authorizer_appid ? (
                  <p className="rounded-lg bg-slate-50 px-3 py-2 font-mono text-xs text-slate-600">
                    AppID：{deleteTarget.authorizer_appid}
                  </p>
                ) : null}
                <ul className="list-inside list-disc space-y-1 text-slate-600">
                  <li>您名下所有站点将无法再向该号推送草稿</li>
                  <li>各站点「本站点推送」开关与关联配置将被清除</li>
                  <li>恢复使用需重新扫码授权或填写 AppSecret</li>
                </ul>
                {(deleteTarget.linked_sites?.length ?? 0) > 0 ? (
                  <p className="rounded-lg border border-amber-200/90 bg-amber-50/90 px-3 py-2 text-amber-950">
                    当前仍有 {deleteTarget.linked_sites!.length} 个站点开启了向该号推送：
                    {deleteTarget.linked_sites!.map((s) => s.site_name).join('、')}
                  </p>
                ) : null}
              </div>
            ) : null}
          </Modal>
        </>
      ) : null}
    </div>
  );
}
